<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();
const route = useRoute();

const handleDelete = async () => {
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: route.params.contactId,
      customAttributes: [props.attribute.attributeKey],
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_ERROR')
    );
  }
};

const handleUpdate = async value => {
  try {
    await store.dispatch('contacts/update', {
      id: route.params.contactId,
      customAttributes: {
        [props.attribute.attributeKey]: value,
      },
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  }
};

const componentMap = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
  default: OtherAttribute,
};

const CurrentAttributeComponent = computed(() => {
  return (
    componentMap[props.attribute.attributeDisplayType] || componentMap.default
  );
});

/** Detecta se o valor já é HTML (para renderizar com v-html) */
const isHTMLContent = computed(() => {
  const v = String(props.attribute?.value ?? '');
  // heurística leve: presença de tags comuns
  return /<(div|span|p|a|br|strong|em)\b/i.test(v);
});
</script>

<template>
  <div
    class="group/attribute w-full"
    :class="isEditingView ? 'min-h-10' : 'min-h-11'"
  >
    <!-- Caso o valor seja HTML, renderiza aqui com v-html -->
    <div v-if="isHTMLContent" class="px-2 py-1">
      <!-- rótulo -->
      <div class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
        {{ attribute.attributeDisplayName }}
      </div>

      <!-- conteúdo HTML -->
      <div
        class="custom-html-content text-sm leading-5"
        v-html="attribute.value"
      ></div>
    </div>

    <!-- Caso não seja HTML, usa o componente padrão -->
    <component
      v-else
      :is="CurrentAttributeComponent"
      :attribute="attribute"
      :is-editing-view="isEditingView"
      @update="handleUpdate"
      @delete="handleDelete"
    />
  </div>
</template>

<style scoped>
.custom-html-content * { max-width: 100%; }
.custom-html-content a { color: #2196F3; text-decoration: none; }
.custom-html-content a:hover { text-decoration: underline; }
</style>
